import { Text } from '@cardboard-ui/react';
import { t } from '@lingui/macro';
import { PublishBar } from 'components/PublishBar';
import React from 'react';
import { RemirrorJSON } from 'remirror';
import { graphql, useFragment, useMutation } from 'utils/graphClient';
import { FolderNodePublishBar_folderNode$key } from './__generated__/FolderNodePublishBar_folderNode.graphql';
import { FolderNodePublishBar_publishFolderNodesMutation } from './__generated__/FolderNodePublishBar_publishFolderNodesMutation.graphql';

const folderNodeFragment = graphql`
  fragment FolderNodePublishBar_folderNode on FolderNodeInterface {
    __typename
    canPublish {
      value
    }
    publishState
    publishedAt
    ... on Folder {
      id
    }
    ... on FolderItem {
      id
      content {
        __typename
      }
      space {
        id
      }
    }
  }
`;

const publishFolderNodesMutation = graphql`
  mutation FolderNodePublishBar_publishFolderNodesMutation(
    $folderNodeId: ID!
    $createAnnouncement: Boolean!
    $spaceId: ID!
    $message: String!
    $announcementConnections: [ID!]!
  ) {
    publishFolderNodes(input: { folderNodeIds: [$folderNodeId] }) {
      folderNodesPublished {
        ...FolderNodePublishBar_folderNode
      }
    }
    createAnnouncement(
      input: { spaceId: $spaceId, message: $message, linkId: $folderNodeId }
    ) @include(if: $createAnnouncement) {
      announcementCreated
        @prependNode(
          connections: $announcementConnections
          edgeTypeName: "Announcement"
        ) {
        id
        ...AnnouncementCard_announcement
      }
    }
  }
`;

interface FolderNodePublishBarProps {
  folderNode: FolderNodePublishBar_folderNode$key;
}

export const FolderNodePublishBar = ({
  folderNode,
}: FolderNodePublishBarProps) => {
  const { __typename, id, canPublish, publishState, content, space } =
    useFragment(folderNodeFragment, folderNode);
  const [publishFolderNode, isPublishLoading] =
    useMutation<FolderNodePublishBar_publishFolderNodesMutation>(
      publishFolderNodesMutation,
    );

  if (!id || !space) {
    return null;
  }

  if (!canPublish.value) {
    return null;
  }

  const onPublish = (announcementMessage?: RemirrorJSON) => {
    const announcementAsString = JSON.stringify(announcementMessage);
    publishFolderNode({
      variables: {
        folderNodeId: id,
        createAnnouncement: !!announcementAsString,
        message: announcementAsString || '',
        spaceId: space.id,
        announcementConnections: [],
      },
    });
  };

  const isPublished = publishState !== 'DRAFT';
  if (__typename === 'FolderItem') {
    if (content?.__typename === 'File') {
      return (
        <PublishBar
          nodeId={id}
          isPublished={isPublished}
          isPublishing={isPublishLoading}
          onPublish={onPublish}
          confirmationTitle={t`Publish File`}
          confirmationContent={
            <Text>{t`People who can access this file's folder will be able to see your file once published.`}</Text>
          }
          spaceId={space.id}
          linkIds={[id]}
        />
      );
    }

    if (content?.__typename === 'Page') {
      return (
        <PublishBar
          nodeId={id}
          isPublished={isPublished}
          isPublishing={isPublishLoading}
          onPublish={onPublish}
          confirmationTitle={t`Publish page`}
          confirmationContent={
            <Text>{t`People who can access this page's folder will be able to see your page once published.`}</Text>
          }
          spaceId={space.id}
          linkIds={[id]}
        />
      );
    }
  }

  if (__typename === 'Folder') {
    return (
      <PublishBar
        nodeId={id}
        isPublished={isPublished}
        isPublishing={isPublishLoading}
        onPublish={onPublish}
        confirmationTitle={t`Publish folder`}
        confirmationContent={
          <Text>{t`People who can access this folder's parent folder will be able to see this folder and its published contents once published.`}</Text>
        }
        spaceId={space.id}
        linkIds={[id]}
      />
    );
  }
  return null;
};
